import { takeEvery, put, call, select } from 'redux-saga/effects';
import {
  setImportRawData,
  setImportRows,
  setImportRowsSkippedCount,
  setImportGeoValidationResult,
  setImportDupCheckErrors,
  appNotify,
} from 'store/actions';
import {
  appSettingsSelector, opportunityImportSelectedChannelSelector, postAcqTaskImportChannelSelector, postAcqTaskImportRequestTypeSelector
} from 'store/selectors';
import { ValidateRows } from 'services/importValidationService';
import { format, addMinutes, getYear, addDays } from 'date-fns';
import { Constants } from 'utilities/constants';
import { PostAcquisitionImportFieldSettings } from 'components/admin/postAcquisitionImport/PostAcquisitionImportFieldSettings';
import { columnSchema as postAcqTaskImportFieldSettings } from 'components/underwrite/postAcquisition/import/importTasks/gridConfig';

function* parsePastedData(importPastedData, eligibleHeaders) {
  try {
    const newImportRows = [];
    const rows = importPastedData.split('\n');
    const cleanedRows = rows.filter(r => r !== '');  // Remove empty rows, this is usually the last row
    if (cleanedRows.length) {
      const headerRow = cleanedRows.shift().split('\t').map(header => header.trim()); // Shift out first row to check for headers
      const foundAtLeastOne = headerRow.some(c => eligibleHeaders.find(el => el.fieldToLowerCase === c.replace(/\s+/g, '').toLowerCase()));

      if (foundAtLeastOne) {
        if (cleanedRows.length) { // Split remaining data rows into cells
          cleanedRows.forEach(row => {
            const cells = row.split('\t');
            const newRow = {};
            headerRow.forEach((col, index) => {
              newRow[col] = cells[index];
            })
            newImportRows.push(newRow);
          });
        } else {
          console.log('No data rows were found');
          yield put(appNotify('No data rows were found', 'warning'));
        }
      } else {
        console.log('No matching headers were found');
        yield put(appNotify('No matching headers were found', 'warning'));
      }
    }
    console.log(newImportRows)
    return newImportRows;
  } catch (error) {
    yield put(appNotify(error.message, 'error'));
    console.log('parsePastedData', error.message);
  }
}

function* cleanData(importType, DragAndDropData, eligibleHeaders) {
  try {
    // Only importing first sheet, should import all?
    // const DragAndDropData = data[Object.keys(data)[0]];
    console.log('saga DragAndDropData', eligibleHeaders, DragAndDropData);
    let rowsSkipped = 0;
    let cleanedRows = [];
    // console.log(new Date(), new Date().getTimezoneOffset())
    DragAndDropData.forEach((row, index) => {
      const rowWithCleanedKeys = {};
      Object.keys(row).forEach(key => { // Remap row object to cleaned keys
        const cleanedKey = key.replace(/\s+/g, '').toLowerCase();
        rowWithCleanedKeys[cleanedKey] = row[key];
      });

      const id = importType === Constants.ImportType.POSTACQTASKS ? 'assetid' : 'sourceid';
      if (!rowWithCleanedKeys[id]) { // Don't add row if it doesn't include id
        rowsSkipped += 1;
        return;
      }
      const gridType = importType === Constants.ImportType.PROPERTIES
        ? 'exploreImportGrid'
        : importType === Constants.ImportType.OPPORTUNITIES
          ? 'importGrid'
          : importType === Constants.ImportType.BACKFILL ? 'backfill' : 'importTitlesGrid';
      // For incoming excel data, null cells simply don't exist in the row object, so we need to loop through the eligibleHeaders list and restore null keys
      const importRow = {};
      eligibleHeaders.forEach(header => {
        const mappedKey = header.field; // Use eligibleHeaders to map cleaned key name to final key name
        let foundValue = null;
        if (rowWithCleanedKeys.hasOwnProperty(header.fieldToLowerCase)) { // Try to find the value in the row data and clean it
          let trimmedCell = rowWithCleanedKeys[header.fieldToLowerCase];
          if (header.type === 'number') {
            trimmedCell = trimmedCell.toString().trim();
            if (trimmedCell !== '') { // Don't operate on empty string or it will be converted to a 0
              const cleanTest = Number(trimmedCell.replace(/[\s$,]+/g, ''));
              if (!isNaN(cleanTest)) { // If removing $ and commas from cell string results in parsable number, use cleaned value. Else, leave original string so that it fails validation later
                trimmedCell = cleanTest.toString();
              }
            }
          } else if (header.type === 'date') {
            if (trimmedCell instanceof Date) {
              if (getYear(trimmedCell) === 1899) { // Excel does not support dates before 1900, causes parser to have rare case where date of 1/1/1900 or 1/0/1900 gets parsed to the year 1899 and one day off. Adding one day to these dates shifts it back to the correct day
                trimmedCell = addDays(trimmedCell, 1);
              }

              let diff = 0; // When the parser is creating JS date objects from the cells, it will automatically assign daylight savings timezone based on the date. This can cause winter dates (not in daylight savings) to be shifted back an hour to 11pm of the previous day.
              if (trimmedCell.getTimezoneOffset() !== new Date().getTimezoneOffset()) { // To fix this, we detect if date is not in same timezone as local machine (due to the daylight savings shifting)...
                diff = trimmedCell.getTimezoneOffset() - new Date().getTimezoneOffset(); // ...get the difference in minutes...
              }
              // console.log(trimmedCell, trimmedCell.toLocaleDateString(), trimmedCell.getTimezoneOffset())
              trimmedCell = format(addMinutes(trimmedCell, diff), 'MM/dd/yyyy'); // ...and add it to the parsed date to shift it back to our local timezone.
            } else {
              trimmedCell = trimmedCell.toString().trim();
            }
          } else if (header.type === 'string') {
            trimmedCell = trimmedCell.toString().trim();
          } else if (header.type === 'boolean') {
            trimmedCell = trimmedCell.toString().trim();
            // If the input was a string (accept yes/no, true/false), convert to bool true/false respectively
            const toLower = trimmedCell.toLowerCase();
            if (toLower === 'yes' || toLower === 'true' || toLower === 'y' || toLower === '1') {
              trimmedCell = true;
            } else if (toLower === 'no' || toLower === 'false' || toLower === 'n' || toLower === '0') {
              trimmedCell = false;
              // } else {
              //   trimmedCell = null; // other values default to null to trigger validation error 
            }
          }
          if (trimmedCell === '') {
            trimmedCell = null;
          } else if (gridType !== 'backfill' && (header[gridType]?.acceptedValues?.length || header.acceptedValues?.length) && typeof trimmedCell === 'string') { // For fields with acceptedValues, attempt to normalize differences in spaces/capitalization
            const cleanedValue = trimmedCell.replace(/\s+/g, '').toLowerCase();
            const acceptedValues = header[gridType]?.acceptedValues || header.acceptedValues;
            const cleanedAcceptedValues = acceptedValues.map(el => el.replace(/\s+/g, '').toLowerCase());
            const index = cleanedAcceptedValues.findIndex(el => el === cleanedValue); // Perform case-insensitive, spaces-removed search for input value in acceptedValues
            if (index > -1) {
              trimmedCell = acceptedValues[index]; // If found, replace input value with properly formatted acceptedValue
            }
          }
          foundValue = trimmedCell;
        }
        importRow[mappedKey] = foundValue; // Add final key name and either cleaned value or null to row
      })
      importRow.referenceId = index + 1;
      if (importType === Constants.ImportType.OPPORTUNITIES || importType === Constants.ImportType.PROPERTIES) {
        importRow.locationVerificationUsed = false;
      }
      cleanedRows.push(importRow);
    });

    const importSelectedChannel = importType === Constants.ImportType.OPPORTUNITIES ? yield select(opportunityImportSelectedChannelSelector) : null;
    const postAcqChannel = importType === Constants.ImportType.POSTACQTASKS ? yield select(postAcqTaskImportChannelSelector) : null;
    const postAcqRequestType = importType === Constants.ImportType.POSTACQTASKS ? yield select(postAcqTaskImportRequestTypeSelector) : null;
    if (importType === Constants.ImportType.BACKFILL) {
      const backfillImportMaxCount = Constants.ImportMaxCount.BACKFILL;
      if (cleanedRows.length > backfillImportMaxCount) {
        yield put(appNotify(`Max row count of ${backfillImportMaxCount} exceeded`, 'warning')); // todo: remove this?
      }
      yield put(setImportRows({ importType, importRows: cleanedRows }))
      return;
    }
    const importSource = importType === Constants.ImportType.POSTACQTASKS ? 'ReVUe' : Constants.ImportSource.ManualImport;
    const importRowsWithValidationResult = yield ValidateRows(cleanedRows, importSelectedChannel, postAcqChannel, postAcqRequestType, importType, importSource);

    if (!importRowsWithValidationResult) return;

    const needsCorrection = importRowsWithValidationResult.some(r => r.invalidColumns?.length > 0);
    const maxRowCount = importType === Constants.ImportType.PROPERTIES
      ? Constants.ImportMaxCount.PROPERTIES
      : importType === Constants.ImportType.POSTACQTASKS
        ? Constants.ImportMaxCount.POSTACQTASKS
        : Constants.ImportMaxCount.OPPORTUNITIES;
    if (importRowsWithValidationResult.length > maxRowCount) {
      yield put(appNotify(`Max row count of ${maxRowCount} exceeded`, 'warning')); // todo: remove this?
    } else if (needsCorrection) {
      yield put(appNotify('Please correct errors in data', 'warning'));
    }

    const actions = [
      setImportRowsSkippedCount({ importType, importRowsSkippedCount: rowsSkipped }),
      setImportRows({ importType, importRows: importRowsWithValidationResult })
    ]

    if (importType !== Constants.ImportType.POSTACQTASKS) {
      actions.push(setImportGeoValidationResult({
        importType,
        importGeoValidationResult: { passedValidation: null, invalidZipCodes: null, invalidCounties: null, invalidGeo: null, geoValidationDtos: null }
      }));
      actions.push(setImportDupCheckErrors({ importType, dupCheckErrors: [] }))
    }
    
    yield put(actions);
  }
  catch (error) {
    yield put(appNotify(error.message, 'error'));
    console.log('cleanData', error.message)
  }
}

export function* importRawDataSaga() {
  yield takeEvery(setImportRawData, function* ({ payload }) {
    const { data, importType, source } = payload;
    try {
      if (data == null) throw new Error('importRawDataSaga received no data');

      const appSettings = yield select(appSettingsSelector);

      const getHeaderType = () => {
        switch (importType) {
          case Constants.ImportType.PROPERTIES:
            return 'exploreImportGrid';
          case Constants.ImportType.OPPORTUNITIES:
            return 'importGrid';
          case Constants.ImportType.BACKFILL:
            return 'backfill';
          case Constants.ImportType.POSTACQTASKS:
            return 'postAcqImportGrid';
          default: return null;
        }
      }
      const headerType = getHeaderType();
      let filteredFieldSettings = appSettings.FieldSettings.filter(el => el[headerType] != null);
      if (headerType === 'importGrid') {
        const importSelectedChannel = yield select(opportunityImportSelectedChannelSelector);
        filteredFieldSettings = filteredFieldSettings.filter(el => el.channels?.includes(importSelectedChannel));
      }
      if(headerType === 'backfill') {
        filteredFieldSettings = PostAcquisitionImportFieldSettings;
      }
      if (headerType === 'postAcqImportGrid') {
        filteredFieldSettings = postAcqTaskImportFieldSettings;
      }
      let myEligibleHeaders = filteredFieldSettings.map(fieldObj => {
        return {
          ...fieldObj,
          fieldToLowerCase: fieldObj.displayName.replace(/\s+/g, '').toLowerCase()
        }
      });
      let myData = data;
      if (source === 'paste') {
        myData = yield call(parsePastedData, data, myEligibleHeaders);
      }
      yield call(cleanData, importType, myData, myEligibleHeaders);
    }
    catch (error) {
      yield put(appNotify(error.message, 'error'));
      console.log('importRawDataSaga', error.message);
    }
  });
}
